/**
 * The audio graph — hand-built on Web Audio, synthesised except where a recording earns
 * its place.
 *
 * Almost everything here is built from noise buffers, filters and oscillators: explosions
 * as long-decay noise with a downward filter sweep, ocean as LFO-filtered noise banks.
 * The exceptions are the sounds the player hears most deliberately — the 30 mm, the rig's
 * horn, the torpedo's launch, run and hit, and the depth charge's two beats — which are
 * owner-supplied recordings, because synthesis got the shape of those right and the
 * character of them wrong. Each loads with a fallback, so a missing file degrades the
 * sound rather than silencing it.
 *
 * The graph is two stages on purpose: every ordinary voice meets at `master`, and volume
 * and mute sit downstream on `output`. That separation is what lets the horn duck the
 * entire mix from outside it without ducking itself, and keeps a duck in progress from
 * fighting the player's volume setting.
 *
 * The two rules that matter most for this game:
 *
 * 1. **Distance is delay plus low-pass.** A shell landing 4 km away is heard about six
 *    seconds later, muffled (sound travels at twice its real speed here — see
 *    SPEED_OF_SOUND). That is a design requirement, not a flourish — it is most of what
 *    sells the scale of the water.
 * 2. **Delayed reports are clustered.** A burst of thirty rounds landing together must
 *    arrive as one weighted report per 250 ms window, never thirty scheduled voices.
 */

import {
  AUTOCANNON_SOUND, TORPEDO_LAUNCH_SOUND, TORPEDO_RUN_SOUND, TORPEDO_HIT_SOUND,
  DEPTH_CHARGE_SOUND, WATER_HIT_SOUND, HULL_HIT_SOUND, waterHitLevel,
} from '../content/weapons';
import {
  HORN_SOUND, CONTACT_PING, BATTLE_AMBIENCE, RICOCHET_SOUND, KRAKEN_CRUSH_SOUND,
  KRAKEN_FINALE_SOUND, COMMS,
} from '../content/audio';
import { CLICK_IN_URL, CLICK_OUT_URL } from '../content/radio';

/**
 * Twice physical (343 m/s), on Adam's call: real acoustics made the wait read as broken
 * at game pace — a 2.4 km torpedo kill sat silent for seven seconds. Doubled, the same
 * kill answers in three and a half, still unmistakably far away. Every distance delay
 * in the engine derives from this one number.
 */
const SPEED_OF_SOUND = 686;
/** Distance band width for clustering delayed reports, metres. */
const CLUSTER_BAND = 400;
/** Window within which impacts merge into a single voice, seconds. */
const CLUSTER_WINDOW = 0.25;

interface PendingCluster {
  count: number;
  distance: number;
  kind: 'water' | 'hull';
  fireAt: number;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  /**
   * The bus every ordinary voice connects to — sea, wind, gun, impacts, alarms.
   *
   * It exists as a distinct stage from the output so the horn can duck the entire rest of
   * the mix without ducking itself. Volume and mute deliberately live downstream on
   * `output`, not here, so a duck in progress can never fight the player's volume setting.
   */
  private master: GainNode | null = null;
  /** Master volume and mute, downstream of the duck bus. The horn taps in here. */
  private output: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private ambienceGain: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  private clusters = new Map<string, PendingCluster>();
  private unlocked = false;
  private muted = false;

  /** The 30 mm takes, once decoded. Empty if loading failed. */
  private gunBuffers: AudioBuffer[] = [];
  /** Which take fires next — strict alternation, so consecutive rounds never repeat. */
  private gunTake = 0;
  /** Live gun voices, so a shift ending can silence tails that are still ringing. */
  private gunVoices: Array<{ src: AudioBufferSourceNode; gain: GainNode }> = [];

  /** The rig's horn, once decoded. Null if loading failed. */
  private hornBuffer: AudioBuffer | null = null;

  // --- The comms channel (see content/radio.ts) ---------------------------------------
  /** The two channel clicks that frame every transmission. */
  private commsClicks: { in: AudioBuffer | null; out: AudioBuffer | null } = { in: null, out: null };
  /** Decoded takes, keyed by url. Loaded per shift and kept for the session. */
  private commsTakes = new Map<string, AudioBuffer>();
  /** Lines waiting for the channel, with the time each was raised. */
  private commsQueue: Array<{ url: string; raisedAt: number; critical: boolean }> = [];
  /** The transmission in progress, if any, and when the channel frees up. */
  private commsBusyUntil = 0;
  private commsOpen = false;
  private commsTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * True once the shift's last word is queued. Nothing may join the channel after it, so
   * a line raised in the final seconds of a fight cannot arrive over the results screen.
   * Cleared when the next shift opens the channel again.
   */
  private commsSealed = false;

  /** The torpedo launch recording, once decoded. Null if loading failed. */
  private torpedoLaunchBuffer: AudioBuffer | null = null;

  /** The looped water-displacement bed under the screw whine. Null if loading failed. */
  private torpedoRunBuffer: AudioBuffer | null = null;

  /** The torpedo's hull-strike recording. Null if loading failed. */
  private torpedoHitBuffer: AudioBuffer | null = null;

  /**
   * The depth charge's three recordings: the charge entering the water, then the two
   * beats of the burst — the sea lifting, and the column breaking through it.
   */
  private depthChargeBuffers: {
    launch: AudioBuffer | null;
    splash: AudioBuffer | null;
    lift: AudioBuffer | null;
    column: AudioBuffer | null;
  } = { launch: null, splash: null, lift: null, column: null };
  /** Guards against a held key stacking horns into a wall of ducking. */
  private hornBusyUntil = 0;

  /** The two water-splash takes for rounds landing inside the audible band. */
  private waterHitBuffers: AudioBuffer[] = [];
  /** Which take plays next — strict alternation, like the gun's. */
  private waterHitTake = 0;
  /** Live splash voices, oldest first, so a long burst sheds its stalest ones. */
  private waterHitVoices: Array<{ src: AudioBufferSourceNode; gain: GainNode }> = [];

  /** Distant gunfire while the convoy is under attack, and its live voice. */
  private battleBuffer: AudioBuffer | null = null;
  private battleVoice: { src: AudioBufferSourceNode; gain: GainNode } | null = null;

  /** Rounds striking the platform's steel — Adam's ricochet takes. Empty if loading failed. */
  private ricochetBuffers: AudioBuffer[] = [];
  private ricochetTake = 0;
  private ricochetVoices: Array<{ src: AudioBufferSourceNode; gain: GainNode }> = [];

  /** The creature crushing the platform, one voice at a time, and when the next may start. */
  private crushBuffers: AudioBuffer[] = [];
  private crushTake = 0;
  private crushVoice: { src: AudioBufferSourceNode; gain: GainNode } | null = null;
  private crushNextAt = 0;

  /** The finale's beats: the assault bed, the stare, and the three one-shots. */
  private climbBuffer: AudioBuffer | null = null;
  private climbVoice: { src: AudioBufferSourceNode; gain: GainNode } | null = null;
  private stareBuffers: AudioBuffer[] = [];
  private stareTake = 0;
  private stareVoice: { src: AudioBufferSourceNode; gain: GainNode } | null = null;
  private stareOn = false;
  private finaleShots: Map<string, AudioBuffer> = new Map();

  /** The 30 mm striking steel. Null if loading failed. */
  private hullHitBuffer: AudioBuffer | null = null;
  /** Live hull-strike voices, oldest first. */
  private hullHitVoices: Array<{ src: AudioBufferSourceNode; gain: GainNode }> = [];

  /** Radar acquisition tones, keyed by whether the contact answered IFF. */
  private pingBuffers: { friendly: AudioBuffer | null; hostile: AudioBuffer | null } =
    { friendly: null, hostile: null };
  /** Next time each tone may sound, so a pack arriving together is one ping. */
  private pingReadyAt = { friendly: 0, hostile: 0 };

  /** Master volume, 0..1, persisted by the settings screen. */
  volume = 0.8;

  /**
   * Build the graph. Must be called from a user gesture — every mobile browser refuses to
   * start an AudioContext otherwise, and iOS additionally needs the session type set or
   * the hardware mute switch silences everything.
   */
  unlock(): void {
    if (this.unlocked) return;

    const Ctor = window.AudioContext
      ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    this.ctx = new Ctor();
    this.unlocked = true;

    // iOS: without this the hardware mute switch kills Web Audio entirely.
    const nav = navigator as unknown as { audioSession?: { type: string } };
    if (nav.audioSession) nav.audioSession.type = 'playback';

    const ctx = this.ctx;
    this.limiter = ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -6;
    this.limiter.knee.value = 6;
    this.limiter.ratio.value = 12;
    this.limiter.attack.value = 0.003;
    this.limiter.release.value = 0.25;

    this.output = ctx.createGain();
    this.output.gain.value = this.volume;

    // Unity at rest; the horn pulls this down and lets it back up.
    this.master = ctx.createGain();
    this.master.gain.value = 1;

    this.master.connect(this.output);
    this.output.connect(this.limiter);
    this.limiter.connect(ctx.destination);

    this.noiseBuffer = this.makeNoiseBuffer(ctx, 2);
    this.startAmbience();
    void this.loadGunSample();
    void this.loadHornSample();
    void this.loadPingSamples();
    void this.loadTorpedoLaunchSample();
    void this.loadTorpedoRunSample();
    void this.loadTorpedoHitSample();
    void this.loadDepthChargeSamples();
    void this.loadWaterHitSamples();
    void this.loadHullHitSample();
    void this.loadBattleAmbience();
    void this.loadRicochetSamples();
    void this.loadKrakenCrushSamples();
    void this.loadKrakenFinaleSamples();

    void ctx.resume();
  }

  /** iOS kills contexts on backgrounding; the app shell calls this on visibility change. */
  resume(): void {
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /**
   * Stop the world's sound while the game is paused.
   *
   * Suspending the context rather than muting the output, because they are not the same
   * thing: muting turns the volume to zero while every voice keeps running, so a paused
   * game silently burned through the tail of a burst, the screw whine of a torpedo still
   * running, and the battle ambience — and un-pausing dropped you back into a soundscape
   * that had moved on without you. Suspension freezes the clock instead, so the sea comes
   * back exactly where it left off.
   */
  suspend(): void {
    if (this.ctx && this.ctx.state === 'running') void this.ctx.suspend();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.output) this.output.gain.value = muted ? 0 : this.volume;
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.output && !this.muted) this.output.gain.value = this.volume;
  }

  /** Drain any clustered impact reports whose arrival time has come. */
  update(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;

    for (const [key, cluster] of this.clusters) {
      if (cluster.fireAt > now) continue;
      this.clusters.delete(key);
      this.playDelayedImpact(cluster);
    }
  }

  /**
   * Fetch and decode the gun recording.
   *
   * Failure is non-fatal by design: everything else in this engine is synthesised, so if
   * the file is missing the weapon simply falls back to the synthesised report rather
   * than the gun going silent.
   */
  private async loadGunSample(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const loaded: AudioBuffer[] = [];
    for (const url of AUTOCANNON_SOUND.urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        loaded.push(await ctx.decodeAudioData(await res.arrayBuffer()));
      } catch (err) {
        console.warn(`[deepwater] ${url} unavailable`, err);
      }
    }
    // Partial success is fine: one take still beats synthesis, it just alternates with
    // itself and leans on the pitch jitter for variation.
    this.gunBuffers = loaded;
    if (loaded.length === 0) {
      console.warn('[deepwater] no 30mm samples loaded; using synthesised report');
    }
  }

  /**
   * The two acquisition tones.
   *
   * No synthesised fallback: unlike the gun and the horn, a missing ping costs the player
   * a notification rather than the character of a sound they hear constantly, and a
   * stand-in beep would be worse than silence.
   */
  private async loadPingSamples(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const load = async (url: string): Promise<AudioBuffer | null> => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return await ctx.decodeAudioData(await res.arrayBuffer());
      } catch (err) {
        console.warn(`[deepwater] ${url} unavailable`, err);
        return null;
      }
    };
    const [friendly, hostile] = await Promise.all([
      load(CONTACT_PING.friendlyUrl),
      load(CONTACT_PING.hostileUrl),
    ]);
    this.pingBuffers = { friendly, hostile };
  }

  /**
   * The radar found something. Called by the sweep as it paints a new contact, never by
   * the detection model directly — the tone is the instrument reporting, so it belongs to
   * the moment the beam arrives rather than the moment the simulation decides.
   */
  contactPing(hostile: boolean): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const key = hostile ? 'hostile' : 'friendly';
    const buffer = this.pingBuffers[key];
    if (!buffer) return;

    const t = ctx.currentTime;
    if (t < this.pingReadyAt[key]) return;
    this.pingReadyAt[key] = t + CONTACT_PING.coalesceWindow;

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.setValueAtTime(CONTACT_PING.gain, t);
    src.connect(g).connect(this.master);
    src.start(t);
  }

  /** Same contract as the gun: if the file is missing, the synthesised two-tone stands in. */
  private async loadHornSample(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      const res = await fetch(HORN_SOUND.url);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      this.hornBuffer = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch (err) {
      console.warn(`[deepwater] ${HORN_SOUND.url} unavailable`, err);
    }
  }

  // --- Weapon and impact voices ---------------------------------------------------------

  /**
   * Fire one round.
   *
   * The recording is a single crack plus its echo over water, so this plays once per
   * round in full and lets the tails overlap — which is what sustained fire actually
   * sounds like out here. Nothing is truncated: the echo is the point.
   */
  private playGunRound(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.gunBuffers.length === 0) return;
    const t = ctx.currentTime;

    const src = ctx.createBufferSource();
    src.buffer = this.gunBuffers[this.gunTake % this.gunBuffers.length] as AudioBuffer;
    this.gunTake++;
    // Small rate variation: identical repeats comb against their own echoes.
    src.playbackRate.value = 1 + (Math.random() * 2 - 1) * AUTOCANNON_SOUND.pitchJitter;

    // Push down everything already ringing so this crack lands on top of the wash
    // rather than inside it.
    for (const v of this.gunVoices) {
      const level = v.gain.gain.value;
      v.gain.gain.cancelScheduledValues(t);
      v.gain.gain.setValueAtTime(level, t);
      v.gain.gain.linearRampToValueAtTime(
        level * AUTOCANNON_SOUND.duckPerShot, t + AUTOCANNON_SOUND.duckTime,
      );
    }

    const g = ctx.createGain();
    g.gain.setValueAtTime(AUTOCANNON_SOUND.gain, t);

    src.connect(g).connect(this.master);
    src.start(t);

    const voice = { src, gain: g };
    this.gunVoices.push(voice);
    src.onended = () => {
      const i = this.gunVoices.indexOf(voice);
      if (i >= 0) this.gunVoices.splice(i, 1);
    };

  }

  /** Silence every gun tail — used when a shift ends or the game is abandoned. */
  stopGunVoices(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    for (const v of this.gunVoices) {
      v.gain.gain.cancelScheduledValues(t);
      v.gain.gain.setValueAtTime(v.gain.gain.value, t);
      v.gain.gain.linearRampToValueAtTime(0, t + 0.12);
      v.src.stop(t + 0.15);
    }
    this.gunVoices = [];
  }

  /** True when at least one take loaded; the caller uses it to decide whether to synthesise. */
  get hasGunSample(): boolean {
    return this.gunBuffers.length > 0;
  }

  /**
   * The gun going off next to the player. Three layers, per the plan's weapon-feel spec:
   * a mechanical action click, the blast itself, and a sub thump you feel more than hear.
   */
  autocannonShot(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;

    // The recording is the sound of this gun. Synthesis is only the fallback for when it
    // failed to load.
    if (this.gunBuffers.length > 0) {
      this.playGunRound();
      return;
    }

    const t = ctx.currentTime;

    // Blast: filtered noise burst with a fast decay.
    const noise = this.noiseSource(ctx);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    // Slight per-shot pitch jitter so a sustained burst never sounds like a loop.
    bp.frequency.value = 1500 + Math.random() * 500;
    bp.Q.value = 0.7;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.55, t);
    g.gain.exponentialRampToValueAtTime(0.0008, t + 0.14);
    noise.connect(bp).connect(g).connect(this.master);
    noise.start(t);
    noise.stop(t + 0.2);

    // Body: a short resonant tone that gives the report a calibre.
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(180 + Math.random() * 30, t);
    osc.frequency.exponentialRampToValueAtTime(70, t + 0.08);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.22, t);
    og.gain.exponentialRampToValueAtTime(0.0005, t + 0.1);
    osc.connect(og).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.12);

    // Sub thump.
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(70, t);
    sub.frequency.exponentialRampToValueAtTime(38, t + 0.12);
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0.34, t);
    sg.gain.exponentialRampToValueAtTime(0.0005, t + 0.16);
    sub.connect(sg).connect(this.master);
    sub.start(t);
    sub.stop(t + 0.18);

    // Mechanical action — the breech working, which is what makes it read as a machine.
    const click = this.noiseSource(ctx);
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 3200;
    const cg = ctx.createGain();
    cg.gain.setValueAtTime(0.1, t + 0.02);
    cg.gain.exponentialRampToValueAtTime(0.0004, t + 0.07);
    click.connect(hp).connect(cg).connect(this.master);
    click.start(t + 0.02);
    click.stop(t + 0.09);
  }

  hostileShot(distance: number): void {
    this.scheduleDistant(distance, () => this.burst(0.3, 900, 0.16, 0.22));
  }

  /**
   * A round into the sea.
   *
   * Shells inside the audible band get Adam's recordings, one per round: that stream of
   * individual splashes is how a player walks a burst onto a target, and clustering it
   * would merge exactly the information they are reading. Everything else — the far
   * water, and the bigger entries — keeps the clustered synthesised report.
   */
  waterImpact(distance: number, big: boolean): void {
    if (!big && this.playWaterHit(distance)) return;
    this.cluster('water', distance, big);
  }

  private async loadWaterHitSamples(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const loaded: AudioBuffer[] = [];
    for (const url of WATER_HIT_SOUND.urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        loaded.push(await ctx.decodeAudioData(await res.arrayBuffer()));
      } catch (err) {
        console.warn(`[deepwater] ${url} unavailable`, err);
      }
    }
    this.waterHitBuffers = loaded;
  }

  /**
   * Play one splash, if it is close enough to be heard. Returns false when it is not,
   * or when the recordings are missing, so the caller falls back to the cluster.
   */
  private playWaterHit(distance: number): boolean {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.waterHitBuffers.length === 0) return false;
    if (distance > WATER_HIT_SOUND.maxRange) return false;

    const level = waterHitLevel(distance) * WATER_HIT_SOUND.gain;
    if (level <= 0) return false;

    const buffer = this.waterHitBuffers[this.waterHitTake % this.waterHitBuffers.length];
    this.waterHitTake++;
    if (!buffer) return false;

    // Shed the oldest voice rather than the newest: in sustained fire the rounds that
    // matter are the ones that just landed.
    this.shedVoices(this.waterHitVoices, WATER_HIT_SOUND.maxVoices);

    this.scheduleDistant(distance, () => {
      if (!ctx || !this.master) return;
      const t = ctx.currentTime;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.playbackRate.value =
        1 + (Math.random() * 2 - 1) * WATER_HIT_SOUND.pitchJitter;
      // Distance still takes the top off, so a splash at 600 m is duller as well as
      // quieter — the same air-absorption rule every other far report obeys.
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = Math.max(900, 6000 * Math.pow(200 / Math.max(1, distance), 0.35));
      const g = ctx.createGain();
      g.gain.setValueAtTime(level, t);
      src.connect(lp).connect(g).connect(this.master);
      src.start(t);
      const voice = { src, gain: g };
      this.waterHitVoices.push(voice);
      src.onended = () => {
        const i = this.waterHitVoices.indexOf(voice);
        if (i >= 0) this.waterHitVoices.splice(i, 1);
      };
    }, Math.random() * WATER_HIT_SOUND.arrivalScatter);
    return true;
  }

  hullImpact(distance: number): void {
    this.cluster('hull', distance, false);
  }

  private async loadBattleAmbience(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      const res = await fetch(BATTLE_AMBIENCE.url);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      this.battleBuffer = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch (err) {
      console.warn(`[deepwater] ${BATTLE_AMBIENCE.url} unavailable`, err);
    }
  }

  /**
   * Raise or lower the distant-battle bed.
   *
   * Called every frame with the range to the engagement, or null when there is none.
   * Idempotent by design: the caller does not track whether a fight is already sounding,
   * it just reports the world each frame and this decides what that means for the mix.
   */
  setBattleAmbience(distance: number | null): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const now = ctx.currentTime;

    if (distance === null) {
      const voice = this.battleVoice;
      if (!voice) return;
      this.battleVoice = null;
      voice.gain.gain.cancelScheduledValues(now);
      voice.gain.gain.setValueAtTime(voice.gain.gain.value, now);
      voice.gain.gain.linearRampToValueAtTime(0.0001, now + BATTLE_AMBIENCE.fadeOut);
      voice.src.stop(now + BATTLE_AMBIENCE.fadeOut + 0.1);
      return;
    }

    const buffer = this.battleBuffer;
    if (!buffer) return;
    const d = Math.max(1, distance);
    const level = BATTLE_AMBIENCE.gain
      * Math.min(1, BATTLE_AMBIENCE.referenceRange / d);

    if (!this.battleVoice) {
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(level, now + BATTLE_AMBIENCE.fadeIn);
      // Distance muffles it the way it muffles everything else out on the water.
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = Math.max(400, 4200 * Math.pow(1800 / d, 0.5));
      src.connect(lp).connect(g).connect(this.master);
      // Start somewhere inside the loop so two engagements in one shift do not open
      // with the same three seconds of audio.
      src.start(now, Math.random() * buffer.duration);
      this.battleVoice = { src, gain: g };
      return;
    }

    // Already sounding: follow the range as the fight moves, gently enough that a
    // vessel manoeuvring does not make the bed pump.
    this.battleVoice.gain.gain.setTargetAtTime(level, now, 0.6);
  }

  private async loadHullHitSample(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      const res = await fetch(HULL_HIT_SOUND.url);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      this.hullHitBuffer = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch (err) {
      console.warn(`[deepwater] ${HULL_HIT_SOUND.url} unavailable`, err);
    }
  }

  /**
   * A 30 mm round finding steel.
   *
   * One voice per round rather than a cluster, for the same reason the splashes get
   * one: the rhythm of hits against misses is the feedback the player is actually
   * reading. Returns false if the recording is missing, so the caller falls back to the
   * clustered synthesised clang.
   */
  hullHit(distance: number): boolean {
    const ctx = this.ctx;
    const buffer = this.hullHitBuffer;
    if (!ctx || !this.master || !buffer) return false;

    const d = Math.max(1, distance);
    const ref = HULL_HIT_SOUND.referenceRange;
    const level = HULL_HIT_SOUND.gain
      * Math.min(1, Math.pow(ref / d, HULL_HIT_SOUND.falloffExponent));
    const cutoff = Math.max(220, 6200 * Math.pow(ref / d, 0.5));
    this.shedVoices(this.hullHitVoices, HULL_HIT_SOUND.maxVoices);

    this.scheduleDistant(d, () => {
      if (!ctx || !this.master) return;
      const t = ctx.currentTime;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.playbackRate.value = 1 + (Math.random() * 2 - 1) * HULL_HIT_SOUND.pitchJitter;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = cutoff;
      const g = ctx.createGain();
      g.gain.setValueAtTime(level, t);
      src.connect(lp).connect(g).connect(this.master);
      src.start(t);
      const voice = { src, gain: g };
      this.hullHitVoices.push(voice);
      src.onended = () => {
        const i = this.hullHitVoices.indexOf(voice);
        if (i >= 0) this.hullHitVoices.splice(i, 1);
      };
    }, Math.random() * HULL_HIT_SOUND.arrivalScatter);
    return true;
  }

  /**
   * Fade out the oldest voices until fewer than `max` remain.
   *
   * Shared by the splash and hull-strike streams: both fire up to five times a second
   * against samples a second or two long, and both want the newest rounds audible
   * rather than the oldest protected.
   */
  private shedVoices(
    voices: Array<{ src: AudioBufferSourceNode; gain: GainNode }>, max: number,
  ): void {
    const ctx = this.ctx;
    if (!ctx) return;
    while (voices.length >= max) {
      const oldest = voices.shift();
      if (!oldest) break;
      const now = ctx.currentTime;
      oldest.gain.gain.cancelScheduledValues(now);
      oldest.gain.gain.setValueAtTime(oldest.gain.gain.value, now);
      oldest.gain.gain.exponentialRampToValueAtTime(0.0004, now + 0.06);
      oldest.src.stop(now + 0.08);
    }
  }

  /**
   * @param depth Metres below the surface. Water is a low-pass filter that gets stronger
   *   with distance travelled through it, so a deep charge arrives as almost pure
   *   sub-bass and a shallow one keeps its crack. This is the audible half of the fuse
   *   selector — the player can hear how deep the charge went off.
   */
  detonation(distance: number, underwater: boolean, depth = 0): void {
    this.scheduleDistant(distance, () => {
      const ctx = this.ctx;
      if (!ctx || !this.master) return;
      const t = ctx.currentTime;

      // Long-decay noise with a downward sweep — the canonical explosion recipe.
      const noise = this.noiseSource(ctx);
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      const muffle = underwater ? Math.max(0.28, 1 - depth / 60) : 1;
      lp.frequency.setValueAtTime(underwater ? 500 * muffle : 2400, t);
      lp.frequency.exponentialRampToValueAtTime(underwater ? 90 * muffle : 200, t + 1.6);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.75, t);
      g.gain.exponentialRampToValueAtTime(0.0008, t + (underwater ? 2.4 : 1.9));
      noise.connect(lp).connect(g).connect(this.master);
      noise.start(t);
      noise.stop(t + 2.6);

      // Sub: the pressure you feel. Underwater detonations are almost entirely this.
      const sub = ctx.createOscillator();
      sub.type = 'sine';
      sub.frequency.setValueAtTime(underwater ? 46 - depth * 0.18 : 62, t);
      sub.frequency.exponentialRampToValueAtTime(22, t + 1.2);
      const sg = ctx.createGain();
      sg.gain.setValueAtTime(underwater ? 0.95 : 0.7, t);
      sg.gain.exponentialRampToValueAtTime(0.0006, t + 1.8);
      sub.connect(sg).connect(this.master);
      sub.start(t);
      sub.stop(t + 2);

      this.duck(1.1);
    });
  }

  /** Both beats; a missing pair falls back to the synthesised underwater detonation. */
  private async loadDepthChargeSamples(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const load = async (url: string): Promise<AudioBuffer | null> => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return await ctx.decodeAudioData(await res.arrayBuffer());
      } catch (err) {
        console.warn(`[deepwater] ${url} unavailable`, err);
        return null;
      }
    };
    const [launch, splash, lift, column] = await Promise.all([
      load(DEPTH_CHARGE_SOUND.launchUrl),
      load(DEPTH_CHARGE_SOUND.splashUrl),
      load(DEPTH_CHARGE_SOUND.liftUrl),
      load(DEPTH_CHARGE_SOUND.columnUrl),
    ]);
    this.depthChargeBuffers = { launch, splash, lift, column };
  }

  /**
   * The depth charge, as the two events the player actually watches.
   *
   * `liftAt` and `columnAt` come from the FX layer's own beat schedule, so the
   * recordings land on the frames that show them; the speed-of-sound delay is added on
   * top of each. Returns false when the recordings are missing, so the caller can fall
   * back to the synthesised burst rather than the weapon going silent.
   */
  depthChargeBurst(distance: number, liftAt: number, columnAt: number): boolean {
    const { lift, column } = this.depthChargeBuffers;
    if (!this.ctx || !this.master || !lift || !column) return false;

    const ref = DEPTH_CHARGE_SOUND.referenceRange;
    this.playRecordedDistant(lift, distance, DEPTH_CHARGE_SOUND.liftGain, liftAt, ref);
    this.playRecordedDistant(
      column, distance, DEPTH_CHARGE_SOUND.columnGain, columnAt, ref,
      () => {
        // Only the eruption ducks the mix; the lift is the quiet half by design.
        if (distance < 2500) this.duck(1.2);
      },
    );
    return true;
  }

  /**
   * The projector firing. Near-field, like the torpedo tube: this happens on the rig
   * the player is standing on, so it takes no distance falloff and no travel delay.
   * Returns false if the recording is missing.
   */
  depthChargeLaunch(): boolean {
    const ctx = this.ctx;
    const buffer = this.depthChargeBuffers.launch;
    if (!ctx || !this.master || !buffer) return false;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.setValueAtTime(DEPTH_CHARGE_SOUND.launchGain, t);
    src.connect(g).connect(this.master);
    src.start(t);
    return true;
  }

  /**
   * The charge hitting the water on its way down — the beat between the lob and the
   * long wait. Returns false if the recording is missing, so the caller can fall back
   * to the synthesised water impact.
   */
  depthChargeSplash(distance: number): boolean {
    const buffer = this.depthChargeBuffers.splash;
    if (!this.ctx || !this.master || !buffer) return false;
    this.playRecordedDistant(
      buffer, distance, DEPTH_CHARGE_SOUND.splashGain, 0,
      DEPTH_CHARGE_SOUND.splashReferenceRange,
    );
    return true;
  }

  /**
   * Play a recording as something heard from `distance` metres, `afterSeconds` after
   * now — one place for the level, the air absorption and the travel delay, so every
   * distant recording in the game obeys the same physics.
   */
  private playRecordedDistant(
    buffer: AudioBuffer,
    distance: number,
    gain: number,
    afterSeconds = 0,
    referenceRange: number = DEPTH_CHARGE_SOUND.referenceRange,
    onPlay?: () => void,
  ): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const d = Math.max(1, distance);
    const ref = referenceRange;
    const level = gain * Math.min(1, ref / d);
    const cutoff = Math.max(180, 5200 * Math.pow(ref / d, 0.55));

    this.scheduleDistant(d, () => {
      if (!ctx || !this.master) return;
      const t = ctx.currentTime;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = cutoff;
      const g = ctx.createGain();
      g.gain.setValueAtTime(level, t);
      src.connect(lp).connect(g).connect(this.master);
      src.start(t);
      onPlay?.();
    }, afterSeconds);
  }

  /** Falls back to the clustered impact report if missing. */
  private async loadTorpedoHitSample(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      const res = await fetch(TORPEDO_HIT_SOUND.url);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      this.torpedoHitBuffer = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch (err) {
      console.warn(`[deepwater] ${TORPEDO_HIT_SOUND.url} unavailable`, err);
    }
  }

  /**
   * The torpedo found a hull.
   *
   * Adam's recording, arriving on the game's sound clock: the flash is instant, the
   * report walks over at SPEED_OF_SOUND — `scheduleDistant` is exact, not thematic.
   * Level and muffling follow the same air-absorption model as every other far-off report
   * (`playDelayedImpact`), so a kill at the horizon is a low distant boom and a kill
   * at 700 m still punches.
   */
  torpedoHit(distance: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    if (!this.torpedoHitBuffer) {
      this.hullImpact(distance);
      return;
    }

    const d = Math.max(1, distance);
    const level = TORPEDO_HIT_SOUND.gain * Math.min(1, 900 / d);
    const cutoff = Math.max(180, 5200 * Math.pow(900 / d, 0.55));

    this.scheduleDistant(d, () => {
      if (!ctx || !this.master || !this.torpedoHitBuffer) return;
      const t = ctx.currentTime;
      const src = ctx.createBufferSource();
      src.buffer = this.torpedoHitBuffer;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = cutoff;
      const g = ctx.createGain();
      g.gain.setValueAtTime(level, t);
      src.connect(lp).connect(g).connect(this.master);
      src.start(t);
      // The payoff moment: push the bed down so the report owns its second.
      if (d < 2500) this.duck(1.0);
    });
  }

  /** No fallback needed: if the file is missing the whine simply runs without its water. */
  private async loadTorpedoRunSample(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      const res = await fetch(TORPEDO_RUN_SOUND.url);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      this.torpedoRunBuffer = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch (err) {
      console.warn(`[deepwater] ${TORPEDO_RUN_SOUND.url} unavailable`, err);
    }
  }

  /** Same contract as the gun and horn: missing file falls back to the synthesised launch. */
  private async loadTorpedoLaunchSample(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      const res = await fetch(TORPEDO_LAUNCH_SOUND.url);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      this.torpedoLaunchBuffer = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch (err) {
      console.warn(`[deepwater] ${TORPEDO_LAUNCH_SOUND.url} unavailable`, err);
    }
  }

  /**
   * Torpedo away.
   *
   * Preferred voice is Adam's layered recording (see `TORPEDO_LAUNCH_SOUND`): tube
   * thump, air charge, splash and tail in one file. The synthesised
   * thump-hiss-splash below is the fallback when the recording is missing.
   */
  torpedoLaunch(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;

    if (this.torpedoLaunchBuffer) {
      const src = ctx.createBufferSource();
      src.buffer = this.torpedoLaunchBuffer;
      const g = ctx.createGain();
      g.gain.setValueAtTime(TORPEDO_LAUNCH_SOUND.gain, t);
      src.connect(g).connect(this.master);
      src.start(t);
      return;
    }

    // The air charge: a pressure thump with almost no top end.
    const thump = ctx.createOscillator();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(120, t);
    thump.frequency.exponentialRampToValueAtTime(38, t + 0.28);
    const tg = ctx.createGain();
    tg.gain.setValueAtTime(0.5, t);
    tg.gain.exponentialRampToValueAtTime(0.0008, t + 0.4);
    thump.connect(tg).connect(this.master);
    thump.start(t);
    thump.stop(t + 0.45);

    // Escaping air over the top of it.
    const hiss = this.noiseSource(ctx);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(1800, t);
    bp.frequency.exponentialRampToValueAtTime(600, t + 0.35);
    bp.Q.value = 0.8;
    const hg = ctx.createGain();
    hg.gain.setValueAtTime(0.22, t);
    hg.gain.exponentialRampToValueAtTime(0.0006, t + 0.5);
    hiss.connect(bp).connect(hg).connect(this.master);
    hiss.start(t);
    hiss.stop(t + 0.55);

    // Water entry, a beat later.
    const splash = this.noiseSource(ctx);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(3200, t + 0.5);
    lp.frequency.exponentialRampToValueAtTime(700, t + 1.1);
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0, t);
    sg.gain.setValueAtTime(0.34, t + 0.5);
    sg.gain.exponentialRampToValueAtTime(0.0006, t + 1.2);
    splash.connect(lp).connect(sg).connect(this.master);
    splash.start(t + 0.5);
    splash.stop(t + 1.3);
  }

  /**
   * A torpedo under way: the synthesised screw whine, with Adam's recorded
   * water-displacement loop underneath it (`TORPEDO_RUN_SOUND`) — the machine, and the
   * sea reacting to the machine. The layers share the launch ramp-in and the stop, but
   * the water starts louder and fades 25% quicker with distance, so a departing fish
   * hands over from rush to pure screw as it goes.
   *
   * One voice per weapon, started at launch and held for the whole run, because the
   * pitch RISING with the acceleration is the entire point — it is the audible half of
   * "a machine building power that you cannot recall". Low-passed hard, since every bit
   * of this is heard through water.
   *
   * Returns a handle the caller stops when the weapon detonates or runs out of fuel; an
   * un-stopped whine would outlive its torpedo and haunt the shift.
   *
   * The level follows distance from the listener, so an outbound fish slowly leaves the
   * mix over its run — by half of `maxRun` it is a presence rather than a sound. The
   * same curve runs in reverse for a hostile torpedo: something quiet in the deep,
   * getting louder.
   */
  torpedoRun(): {
    setSpeed: (speed01: number) => void;
    setDistance: (metres: number) => void;
    stop: () => void;
  } | null {
    const ctx = this.ctx;
    if (!ctx || !this.master) return null;
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(58, t);

    // A second oscillator slightly detuned gives the beating a real screw has.
    const beat = ctx.createOscillator();
    beat.type = 'sawtooth';
    beat.frequency.setValueAtTime(61, t);

    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(420, t);
    lp.Q.value = 2.2;

    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.13, t + 0.6);

    // Distance sits on its own stage so the fade never fights the launch ramp-in above
    // or the stop ramp below — three independent envelopes on one voice. The water bed
    // has its own, faster-falling stage below.
    const distG = ctx.createGain();
    distG.gain.setValueAtTime(1, t);

    osc.connect(lp);
    beat.connect(lp);
    lp.connect(g).connect(distG).connect(this.master);
    osc.start(t);
    beat.start(t);

    // The water bed: a seamless loop with its own copy of the launch envelope, and its
    // own distance stage so it can fade quicker than the screw. Started at a random
    // point in the loop so two fish in the water never phase-align.
    let water: AudioBufferSourceNode | null = null;
    let waterG: GainNode | null = null;
    let waterDist: GainNode | null = null;
    if (this.torpedoRunBuffer) {
      water = ctx.createBufferSource();
      water.buffer = this.torpedoRunBuffer;
      water.loop = true;
      waterG = ctx.createGain();
      waterG.gain.setValueAtTime(0, t);
      waterG.gain.linearRampToValueAtTime(TORPEDO_RUN_SOUND.gain, t + 0.6);
      waterDist = ctx.createGain();
      waterDist.gain.setValueAtTime(1, t);
      water.connect(waterG).connect(waterDist).connect(this.master);
      water.start(t, Math.random() * this.torpedoRunBuffer.duration);
    }

    return {
      setSpeed: (speed01: number) => {
        const now = ctx.currentTime;
        const clamped = Math.max(0, Math.min(1, speed01));
        // Roughly an octave and a half across the acceleration phase.
        osc.frequency.setTargetAtTime(52 + clamped * 96, now, 0.25);
        beat.frequency.setTargetAtTime(55 + clamped * 101, now, 0.25);
        lp.frequency.setTargetAtTime(380 + clamped * 900, now, 0.3);
        // The water rushes a touch faster with the screw — ±10%, enough to connect the
        // layers without pitching noise into something synthetic.
        water?.playbackRate.setTargetAtTime(0.9 + clamped * 0.2, now, 0.3);
      },
      setDistance: (metres: number) => {
        const now = ctx.currentTime;
        const d = Math.max(0, metres);
        // Inverse-power falloff, calibrated to the weapon's 4 km maxRun: full in close,
        // ~0.36 at 600 m, ~0.2 at a typical 1.2 km shot, ~0.06 as the fuel runs out.
        // Gentler than hard inverse-square because the whine starts quiet — real
        // attenuation rates would make it vanish before the wait has done its work.
        // Reference distance was 600; Adam asked for a 20% faster fade after hearing it.
        distG.gain.setTargetAtTime(1 / Math.pow(1 + d / 500, 1.3), now, 0.35);
        // The water bed on the same curve compressed 25% (ref 500 -> 400), per Adam:
        // louder than the screw close in, gone from under it sooner going away.
        waterDist?.gain.setTargetAtTime(1 / Math.pow(1 + d / 400, 1.3), now, 0.35);
      },
      stop: () => {
        const now = ctx.currentTime;
        g.gain.cancelScheduledValues(now);
        g.gain.setValueAtTime(g.gain.value, now);
        g.gain.exponentialRampToValueAtTime(0.0004, now + 0.25);
        osc.stop(now + 0.3);
        beat.stop(now + 0.3);
        if (water && waterG) {
          waterG.gain.cancelScheduledValues(now);
          waterG.gain.setValueAtTime(waterG.gain.value, now);
          waterG.gain.exponentialRampToValueAtTime(0.0004, now + 0.25);
          water.stop(now + 0.3);
        }
      },
    };
  }

  private async loadRicochetSamples(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const loaded: AudioBuffer[] = [];
    for (const url of RICOCHET_SOUND.urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        loaded.push(await ctx.decodeAudioData(await res.arrayBuffer()));
      } catch (err) {
        console.warn(`[deepwater] ${url} unavailable`, err);
      }
    }
    this.ricochetBuffers = loaded;
  }

  /**
   * A round striking the platform's own steel — Adam's recorded ricochets.
   *
   * No distance model and no speed-of-sound delay: the impact is the structure the
   * player is standing on. A small random onset scatter keeps a pair of rounds landing
   * on the same sim tick from stacking into one doubled hit. Returns false when the
   * takes are not available, so the synthesised clang can stand in.
   */
  rigBulletHit(): boolean {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.ricochetBuffers.length === 0) return false;

    const buffer = this.ricochetBuffers[this.ricochetTake % this.ricochetBuffers.length];
    this.ricochetTake++;
    if (!buffer) return false;

    this.shedVoices(this.ricochetVoices, RICOCHET_SOUND.maxVoices);

    const t = ctx.currentTime + Math.random() * RICOCHET_SOUND.scatter;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = 1 + (Math.random() * 2 - 1) * RICOCHET_SOUND.pitchJitter;
    const g = ctx.createGain();
    g.gain.setValueAtTime(RICOCHET_SOUND.gain, t);
    src.connect(g).connect(this.master);
    src.start(t);

    const voice = { src, gain: g };
    this.ricochetVoices.push(voice);
    src.onended = () => {
      const i = this.ricochetVoices.indexOf(voice);
      if (i >= 0) this.ricochetVoices.splice(i, 1);
    };
    return true;
  }

  private async loadKrakenCrushSamples(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const loaded: AudioBuffer[] = [];
    for (const url of KRAKEN_CRUSH_SOUND.urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        loaded.push(await ctx.decodeAudioData(await res.arrayBuffer()));
      } catch (err) {
        console.warn(`[deepwater] ${url} unavailable`, err);
      }
    }
    this.crushBuffers = loaded;
  }

  /**
   * The creature crushing the platform — Adam's recorded takes, one voice at a time.
   *
   * Adam's rule enforced here: never two at once, and `minGap` of clear air after a
   * take ends before the next may begin. A grapple that lands inside that window
   * returns true without playing anything — the steel is already groaning, and that
   * take is its sound. Returns false only when the recordings are unavailable, so the
   * synthesised clang can stand in.
   */
  krakenCrush(): boolean {
    const ctx = this.ctx;
    if (!ctx || !this.master || this.crushBuffers.length === 0) return false;
    if (ctx.currentTime < this.crushNextAt) return true;

    const buffer = this.crushBuffers[this.crushTake % this.crushBuffers.length];
    this.crushTake++;
    if (!buffer) return false;

    const t = ctx.currentTime;
    const rate = 1 + (Math.random() * 2 - 1) * KRAKEN_CRUSH_SOUND.pitchJitter;
    this.crushNextAt = t + buffer.duration / rate + KRAKEN_CRUSH_SOUND.minGap;

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = rate;
    const g = ctx.createGain();
    g.gain.setValueAtTime(KRAKEN_CRUSH_SOUND.gain, t);
    src.connect(g).connect(this.master);
    src.start(t);

    const voice = { src, gain: g };
    this.crushVoice = voice;
    src.onended = () => {
      if (this.crushVoice === voice) this.crushVoice = null;
    };
    return true;
  }

  private async loadKrakenFinaleSamples(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const decode = async (url: string): Promise<AudioBuffer | null> => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return await ctx.decodeAudioData(await res.arrayBuffer());
      } catch (err) {
        console.warn(`[deepwater] ${url} unavailable`, err);
        return null;
      }
    };

    this.climbBuffer = await decode(KRAKEN_FINALE_SOUND.climb.url);
    const stare: AudioBuffer[] = [];
    for (const url of KRAKEN_FINALE_SOUND.eyeContact.urls) {
      const buffer = await decode(url);
      if (buffer) stare.push(buffer);
    }
    this.stareBuffers = stare;
    for (const one of [
      KRAKEN_FINALE_SOUND.headReveal,
      KRAKEN_FINALE_SOUND.dying,
      KRAKEN_FINALE_SOUND.impact,
    ]) {
      const buffer = await decode(one.url);
      if (buffer) this.finaleShots.set(one.url, buffer);
    }
  }

  /**
   * The assault bed: limbs working the structure, for as long as they are on it.
   *
   * Idempotent like the battle bed — the caller reports the world each frame and this
   * decides what that means for the mix, so no one has to remember whether it is already
   * sounding.
   */
  setKrakenClimb(on: boolean): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const now = ctx.currentTime;
    const cfg = KRAKEN_FINALE_SOUND.climb;

    if (!on) {
      const voice = this.climbVoice;
      if (!voice) return;
      this.climbVoice = null;
      voice.gain.gain.cancelScheduledValues(now);
      voice.gain.gain.setValueAtTime(voice.gain.gain.value, now);
      voice.gain.gain.linearRampToValueAtTime(0.0001, now + cfg.fadeOut);
      voice.src.stop(now + cfg.fadeOut + 0.1);
      return;
    }

    if (this.climbVoice || !this.climbBuffer) return;
    const src = ctx.createBufferSource();
    src.buffer = this.climbBuffer;
    src.loop = true;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(cfg.gain, now + cfg.fadeIn);
    src.connect(g).connect(this.master);
    src.start(now);
    this.climbVoice = { src, gain: g };
  }

  /**
   * The stare: two takes chained end to end while the eye holds the player.
   *
   * `onended` re-arms the next take rather than a timer, so the pair runs continuously
   * with no seam and no drift, and stops the moment the phase does.
   */
  setKrakenStare(on: boolean): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    this.stareOn = on;

    if (!on) {
      const voice = this.stareVoice;
      this.stareVoice = null;
      if (!voice) return;
      const now = ctx.currentTime;
      voice.gain.gain.cancelScheduledValues(now);
      voice.gain.gain.setValueAtTime(voice.gain.gain.value, now);
      voice.gain.gain.linearRampToValueAtTime(
        0.0001, now + KRAKEN_FINALE_SOUND.eyeContact.fadeOut,
      );
      voice.src.stop(now + KRAKEN_FINALE_SOUND.eyeContact.fadeOut + 0.1);
      return;
    }

    if (this.stareVoice) return;
    this.playStareTake();
  }

  private playStareTake(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.stareOn || this.stareBuffers.length === 0) return;
    const buffer = this.stareBuffers[this.stareTake % this.stareBuffers.length];
    this.stareTake++;
    if (!buffer) return;

    const now = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.setValueAtTime(KRAKEN_FINALE_SOUND.eyeContact.gain, now);
    src.connect(g).connect(this.master);
    src.start(now);

    const voice = { src, gain: g };
    this.stareVoice = voice;
    src.onended = () => {
      if (this.stareVoice !== voice) return;
      this.stareVoice = null;
      // Straight into the other take, so the pair reads as one held breath.
      this.playStareTake();
    };
  }

  /** One of the finale's three one-shots: the head coming up, the death, the impact. */
  private krakenOneShot(cfg: { url: string; gain: number }): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const buffer = this.finaleShots.get(cfg.url);
    if (!buffer) return;
    const now = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.setValueAtTime(cfg.gain, now);
    src.connect(g).connect(this.master);
    src.start(now);
  }

  /** The head rising into view. */
  krakenHeadReveal(): void {
    this.krakenOneShot(KRAKEN_FINALE_SOUND.headReveal);
  }

  /** The eye rolling back, the mass going over. */
  krakenDying(): void {
    this.krakenOneShot(KRAKEN_FINALE_SOUND.dying);
  }

  /** The body meeting the sea. */
  krakenOceanImpact(): void {
    this.krakenOneShot(KRAKEN_FINALE_SOUND.impact);
  }

  /**
   * Silence every creature voice — a shift ending mid-fight must not groan, climb or
   * stare over the report.
   */
  stopCreatureVoices(): void {
    const ctx = this.ctx;
    this.setKrakenClimb(false);
    this.setKrakenStare(false);
    const voice = this.crushVoice;
    this.crushNextAt = 0;
    if (!ctx || !voice) return;
    const now = ctx.currentTime;
    voice.gain.gain.cancelScheduledValues(now);
    voice.gain.gain.setValueAtTime(voice.gain.gain.value, now);
    voice.gain.gain.exponentialRampToValueAtTime(0.0005, now + 0.25);
    voice.src.stop(now + 0.3);
    this.crushVoice = null;
  }

  rigHit(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    // Structural: a low resonant clang through steel.
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(140, t);
    osc.frequency.exponentialRampToValueAtTime(52, t + 0.9);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.5, t);
    g.gain.exponentialRampToValueAtTime(0.0006, t + 1.4);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 1.5);
    this.burst(0.35, 700, 0.5, 0.3);
  }

  reload(): void {
    this.burst(0.16, 2600, 0.12, 0.1);
  }

  /**
   * The rig's horn — sounded on H, and automatically when a vessel reaches safety.
   *
   * The recording plays whole against the output stage rather than the shared bus, and
   * pulls that bus down underneath itself. Routing it through the same bus it is ducking
   * would simply make it quieter along with everything else, which is the mistake that
   * makes ducked mixes sound merely muddy.
   */
  klaxon(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.output) return;

    if (!this.hornBuffer) {
      this.synthesisedKlaxon();
      return;
    }

    const t = ctx.currentTime;
    // A horn is a held note, not a trigger. Retriggering it mid-blast stacks ducks and
    // the mix pumps; a second press during a blast is simply the same blast.
    if (t < this.hornBusyUntil) return;
    const duration = this.hornBuffer.duration;
    this.hornBusyUntil = t + duration * 0.72;

    const src = ctx.createBufferSource();
    src.buffer = this.hornBuffer;
    const g = ctx.createGain();
    g.gain.setValueAtTime(HORN_SOUND.gain, t);
    src.connect(g).connect(this.output);
    src.start(t);

    // Duck the rest of the mix for the blast, then let it back up under the tail.
    const bus = this.master.gain;
    const releaseAt = Math.max(t + HORN_SOUND.duckAttack, t + duration - HORN_SOUND.duckRelease);
    bus.cancelScheduledValues(t);
    bus.setValueAtTime(bus.value, t);
    bus.linearRampToValueAtTime(HORN_SOUND.duckOthers, t + HORN_SOUND.duckAttack);
    bus.setValueAtTime(HORN_SOUND.duckOthers, releaseAt);
    bus.linearRampToValueAtTime(1, releaseAt + HORN_SOUND.duckRelease);
  }

  /** Fallback two tones, the second a fifth up — used only if the recording is missing. */
  private synthesisedKlaxon(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    for (const [i, freq] of [440, 660].entries()) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = freq;
      const filt = ctx.createBiquadFilter();
      filt.type = 'lowpass';
      filt.frequency.value = 1800;
      const g = ctx.createGain();
      const start = t + i * 0.34;
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(0.22, start + 0.04);
      g.gain.setValueAtTime(0.22, start + 0.24);
      g.gain.exponentialRampToValueAtTime(0.0005, start + 0.34);
      osc.connect(filt).connect(g).connect(this.master);
      osc.start(start);
      osc.stop(start + 0.4);
    }
  }

  // --- Ambience ----------------------------------------------------------------------------

  /**
   * The bed: two noise banks, one low and slow for the sea, one higher and faster for
   * wind through structure, each with a wandering filter so the loop never announces
   * itself. Synthesised beds mean zero download and a genuinely seamless loop.
   */
  private startAmbience(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.noiseBuffer) return;

    this.ambienceGain = ctx.createGain();
    this.ambienceGain.gain.value = 0.24;
    this.ambienceGain.connect(this.master);

    // Sea: low rumble with a slowly swelling filter — the swell breathing.
    const sea = ctx.createBufferSource();
    sea.buffer = this.noiseBuffer;
    sea.loop = true;
    const seaFilter = ctx.createBiquadFilter();
    seaFilter.type = 'lowpass';
    seaFilter.frequency.value = 320;
    seaFilter.Q.value = 0.6;
    const seaLfo = ctx.createOscillator();
    seaLfo.frequency.value = 0.07;
    const seaLfoGain = ctx.createGain();
    seaLfoGain.gain.value = 170;
    seaLfo.connect(seaLfoGain).connect(seaFilter.frequency);
    sea.connect(seaFilter).connect(this.ambienceGain);
    sea.start();
    seaLfo.start();

    // Wind: band-passed hiss with gusting.
    const wind = ctx.createBufferSource();
    wind.buffer = this.noiseBuffer;
    wind.loop = true;
    const windFilter = ctx.createBiquadFilter();
    windFilter.type = 'bandpass';
    windFilter.frequency.value = 900;
    windFilter.Q.value = 0.8;
    const windGain = ctx.createGain();
    windGain.gain.value = 0.34;
    const gustLfo = ctx.createOscillator();
    gustLfo.frequency.value = 0.13;
    const gustGain = ctx.createGain();
    gustGain.gain.value = 0.2;
    gustLfo.connect(gustGain).connect(windGain.gain);
    wind.connect(windFilter).connect(windGain).connect(this.ambienceGain);
    wind.start();
    gustLfo.start();
  }

  /** Weather drives how loud and how bright the bed is. */
  setAmbience(seaState: number, windSpeed: number): void {
    if (!this.ambienceGain) return;
    const intensity = Math.min(1, (seaState - 3) / 4);
    const wind = Math.min(1, windSpeed / 28);
    this.ambienceGain.gain.value = 0.16 + intensity * 0.2 + wind * 0.1;
  }

  // --- Internals ------------------------------------------------------------------------------

  private makeNoiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
    const length = Math.floor(ctx.sampleRate * seconds);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    // Brown-ish noise: integrating white noise gives a spectrum much closer to sea and
    // wind than flat white, which sounds like radio static.
    let last = 0;
    for (let i = 0; i < length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.2;
    }
    return buffer;
  }

  private noiseSource(ctx: AudioContext): AudioBufferSourceNode {
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    // Start from a random offset so repeated bursts never share a waveform.
    src.loop = true;
    return src;
  }

  /** Generic filtered noise burst. */
  private burst(gain: number, frequency: number, decay: number, lowpassQ: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    const noise = this.noiseSource(ctx);
    const filt = ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = frequency;
    filt.Q.value = lowpassQ;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0005, t + decay);
    noise.connect(filt).connect(g).connect(this.master);
    noise.start(t);
    noise.stop(t + decay + 0.05);
  }

  /**
   * Merge impacts into per-band, per-window clusters.
   *
   * Thirty rounds walking across a hull is one event to the ear, not thirty. Each cluster
   * counts its hits and plays a single voice scaled by that count when its delayed
   * arrival time comes round.
   */
  private cluster(kind: 'water' | 'hull', distance: number, big: boolean): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const band = Math.floor(distance / CLUSTER_BAND);
    const key = `${kind}:${band}`;
    const delay = distance / SPEED_OF_SOUND;

    const existing = this.clusters.get(key);
    if (existing && existing.fireAt - ctx.currentTime < CLUSTER_WINDOW) {
      existing.count += big ? 3 : 1;
      return;
    }
    this.clusters.set(key, {
      count: big ? 3 : 1,
      distance,
      kind,
      fireAt: ctx.currentTime + delay,
    });
  }

  private playDelayedImpact(cluster: PendingCluster): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;

    // Distance attenuation and air absorption. Beyond a kilometre almost nothing above a
    // couple of kHz survives the trip.
    const d = Math.max(1, cluster.distance);
    const level = Math.min(1, 900 / d) * Math.min(1.4, 0.4 + cluster.count * 0.18);
    const cutoff = Math.max(180, 5200 * Math.pow(900 / d, 0.55));

    const t = ctx.currentTime;
    const noise = this.noiseSource(ctx);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = cutoff;
    const g = ctx.createGain();
    const decay = cluster.kind === 'water' ? 0.55 : 0.3;
    g.gain.setValueAtTime(level * (cluster.kind === 'water' ? 0.42 : 0.3), t);
    g.gain.exponentialRampToValueAtTime(0.0005, t + decay);
    noise.connect(lp).connect(g).connect(this.master);
    noise.start(t);
    noise.stop(t + decay + 0.05);

    // A water column has a low thump under the hiss; metal does not.
    if (cluster.kind === 'water' && d < 3000) {
      const sub = ctx.createOscillator();
      sub.type = 'sine';
      sub.frequency.setValueAtTime(90, t);
      sub.frequency.exponentialRampToValueAtTime(45, t + 0.3);
      const sg = ctx.createGain();
      sg.gain.setValueAtTime(level * 0.3, t);
      sg.gain.exponentialRampToValueAtTime(0.0005, t + 0.35);
      sub.connect(sg).connect(this.master);
      sub.start(t);
      sub.stop(t + 0.4);
    }
  }

  /**
   * Schedule a one-shot to arrive at the speed of sound, optionally after a further
   * delay of its own — the depth charge's beats happen a beat or two after the event
   * that spawned them, and each still has to travel.
   */
  private scheduleDistant(distance: number, play: () => void, afterSeconds = 0): void {
    const delayMs = (distance / SPEED_OF_SOUND + afterSeconds) * 1000;
    if (delayMs < 30) { play(); return; }
    window.setTimeout(play, delayMs);
  }

  /** Duck the ambience bed briefly after a big moment, then bring it back. */
  private duck(seconds: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.ambienceGain) return;
    const t = ctx.currentTime;
    const current = this.ambienceGain.gain.value;
    this.ambienceGain.gain.cancelScheduledValues(t);
    this.ambienceGain.gain.setValueAtTime(current, t);
    this.ambienceGain.gain.linearRampToValueAtTime(current * 0.25, t + 0.08);
    this.ambienceGain.gain.setValueAtTime(current * 0.25, t + seconds * 0.5);
    this.ambienceGain.gain.linearRampToValueAtTime(current, t + seconds);
  }

  // --- The comms channel ----------------------------------------------------------------

  /**
   * Preload the takes a shift can use, plus the two channel clicks.
   *
   * Per shift rather than up front: the whole campaign is a few megabytes of speech, and
   * none of it is needed until the shift that says it starts. Failures are logged and
   * dropped — a missing take costs one line of atmosphere, and the text is on screen
   * regardless, so it must never be allowed to stop a shift starting.
   */
  async loadComms(urls: readonly string[]): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const decode = async (url: string): Promise<AudioBuffer | null> => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return await ctx.decodeAudioData(await res.arrayBuffer());
      } catch (err) {
        console.warn(`[deepwater] comms ${url} unavailable`, err);
        return null;
      }
    };

    if (!this.commsClicks.in) {
      const [click_in, click_out] = await Promise.all([
        decode(CLICK_IN_URL), decode(CLICK_OUT_URL),
      ]);
      this.commsClicks = { in: click_in, out: click_out };
    }

    const wanted = urls.filter((u) => !this.commsTakes.has(u));
    await Promise.all(wanted.map(async (url) => {
      const buf = await decode(url);
      if (buf) this.commsTakes.set(url, buf);
    }));
  }

  /**
   * Speak a line. `critical` lines always play; ordinary ones are dropped if the channel
   * has fallen too far behind the fight.
   */
  radio(url: string, critical = false): void {
    if (!this.ctx || !this.master || this.commsSealed) return;
    this.commsQueue.push({ url, raisedAt: this.ctx.currentTime, critical });
    this.pumpComms();
  }

  /**
   * The shift's last word.
   *
   * It jumps whatever is still queued and seals the channel behind it, which is what stops
   * the operator narrating the fight over the results screen. The backlog it drops is by
   * definition chatter about a shift that is now decided — the text was on screen when it
   * mattered, and the sign-off is the only line still worth hearing.
   */
  radioFinal(url: string): void {
    if (!this.ctx || !this.master || this.commsSealed) return;
    this.commsQueue.length = 0;
    this.commsQueue.push({ url, raisedAt: this.ctx.currentTime, critical: true });
    this.commsSealed = true;
    this.pumpComms();
  }

  /**
   * Close the channel with nothing more to say — defeat, the watchdog, any ending that
   * arrives without a sign-off. A line already sounding finishes; nothing new starts.
   * A sign-off that has already sealed the channel keeps the last word.
   */
  sealComms(): void {
    if (this.commsSealed) return;
    this.commsSealed = true;
    this.commsQueue.length = 0;
  }

  /** Drop everything queued and reopen the channel — starting a shift, or leaving to the menu. */
  clearComms(): void {
    this.commsQueue.length = 0;
    this.commsSealed = false;
    if (this.commsTimer !== null) { clearTimeout(this.commsTimer); this.commsTimer = null; }
  }

  private pumpComms(): void {
    const ctx = this.ctx;
    if (!ctx || !this.output || this.commsTimer !== null) return;
    const t = ctx.currentTime;
    if (t < this.commsBusyUntil) {
      this.commsTimer = setTimeout(() => {
        this.commsTimer = null;
        this.pumpComms();
      }, Math.max(30, (this.commsBusyUntil - t) * 1000));
      return;
    }

    /*
     * Drop lines the fight has outrun.
     *
     * Text is instant and speech is not, so in a dense stretch the queue falls behind —
     * and a voice calmly announcing a contact the player killed fifteen seconds ago
     * breaks the fiction harder than saying nothing. The text was shown when it mattered;
     * nothing is lost by letting the audio go.
     */
    while (this.commsQueue.length > 1) {
      const head = this.commsQueue[0] as { url: string; raisedAt: number; critical: boolean };
      if (head.critical || t - head.raisedAt < COMMS.staleSeconds) break;
      this.commsQueue.shift();
    }

    const next = this.commsQueue.shift();
    if (!next) { this.closeChannel(); return; }
    const take = this.commsTakes.get(next.url);
    if (!take) { this.pumpComms(); return; }

    /*
     * The channel stays open between lines that are already queued. Scripts author beats
     * two to five seconds apart, and clicking out and back in between them sounds like a
     * radio fault rather than one person still talking.
     */
    let at = t + 0.02;
    if (!this.commsOpen) {
      at += this.playClick(this.commsClicks.in, at);
      this.commsOpen = true;
    }

    const src = ctx.createBufferSource();
    src.buffer = take;
    /*
     * A band-pass and a little drive, so the voice arrives over a radio rather than from
     * inside the player's head. Applied here rather than baked into the file: the takes
     * stay clean, and the treatment can be retuned without re-recording sixty-three lines.
     */
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = COMMS.centreHz;
    band.Q.value = COMMS.q;
    const shaper = ctx.createWaveShaper();
    shaper.curve = this.commsDriveCurve();
    const gain = ctx.createGain();
    gain.gain.value = COMMS.gain;
    src.connect(band).connect(shaper).connect(gain).connect(this.output);
    src.start(at);

    const ends = at + take.duration;
    this.commsBusyUntil = ends;
    this.duckForComms(at, ends);
    this.pumpComms();
  }

  /** Close the channel once nothing more is waiting. */
  private closeChannel(): void {
    const ctx = this.ctx;
    if (!ctx || !this.commsOpen) return;
    this.commsOpen = false;
    this.playClick(this.commsClicks.out, Math.max(ctx.currentTime, this.commsBusyUntil) + 0.05);
  }

  /** Returns the click's duration so the line can start after it. */
  private playClick(buffer: AudioBuffer | null, at: number): number {
    const ctx = this.ctx;
    if (!ctx || !this.output || !buffer) return 0;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.value = COMMS.clickGain;
    src.connect(g).connect(this.output);
    src.start(at);
    return buffer.duration;
  }

  /**
   * Pull the rest of the mix down under a transmission.
   *
   * Same routing the horn uses: the voice plays against `output` and ducks `master` from
   * outside it, so it never ducks itself and the player's volume setting still wins.
   */
  private duckForComms(from: number, to: number): void {
    if (!this.master) return;
    const bus = this.master.gain;
    bus.cancelScheduledValues(from);
    bus.setValueAtTime(bus.value, from);
    bus.linearRampToValueAtTime(COMMS.duckOthers, from + 0.12);
    bus.setValueAtTime(COMMS.duckOthers, Math.max(from + 0.12, to - 0.1));
    bus.linearRampToValueAtTime(1, to + 0.25);
  }

  private commsShaper: Float32Array<ArrayBuffer> | null = null;
  private commsDriveCurve(): Float32Array<ArrayBuffer> {
    if (this.commsShaper) return this.commsShaper;
    const n = 1024;
    const curve = new Float32Array(new ArrayBuffer(n * 4));
    for (let i = 0; i < n; i++) {
      const x = (i / (n - 1)) * 2 - 1;
      curve[i] = Math.tanh(x * COMMS.drive) / Math.tanh(COMMS.drive);
    }
    this.commsShaper = curve;
    return curve;
  }

  dispose(): void {
    this.stopGunVoices();
    this.clearComms();
    void this.ctx?.close();
    this.ctx = null;
    this.unlocked = false;
  }
}
